import { Button, Form, Input, InputNumber, Modal } from 'antd';
import { useState } from 'react';
import type { JobAccessorialTerm } from '../../../jobs.api';

interface OverrideFormValues {
  freeQuantity: number;
  reason: string;
}

interface JobAccessorialOverrideModalProps {
  term: JobAccessorialTerm | null;
  onCancel: () => void;
  onSubmit: (freeQuantity: number, reason: string) => Promise<boolean>;
}

/** Corrects the free hours/days copied onto a job — the reason is mandatory because it overrides what the customer's rates said (gitaumoses4/chukua#33). */
export function JobAccessorialOverrideModal({ term, onCancel, onSubmit }: JobAccessorialOverrideModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<OverrideFormValues>();
  const unitLabel = term?.unit === 'per_day' ? 'days' : 'hours';

  async function submit(values: OverrideFormValues) {
    setSubmitting(true);
    try {
      if (await onSubmit(values.freeQuantity, values.reason)) form.resetFields();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={term !== null} title={`Change free ${unitLabel}: ${term?.accessorialTypeName ?? ''}`} onCancel={onCancel} footer={null} destroyOnClose>
      <Form<OverrideFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={submit}
        disabled={submitting}
        initialValues={{ freeQuantity: term?.effectiveFreeQuantity ?? 0 }}
      >
        <Form.Item name="freeQuantity" label={`Free ${unitLabel}`} rules={[{ required: true, message: 'Enter a number' }]}>
          <InputNumber className="w-full" min={0} precision={0} />
        </Form.Item>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Enter a reason' }]}>
          <Input.TextArea rows={3} maxLength={500} />
        </Form.Item>
        <Form.Item className="mb-0 flex justify-end">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
